/**
 * The blocks a creator reached for last.
 *
 * The picker offers eighteen rows in five labelled sections, which is fine to
 * browse and tedious to use: the same four exercises get added over and over, and
 * each one costs a scroll to its section. So the sheet remembers the keys a
 * creator actually chose and offers them back — newest first, in its own
 * "Recently used" group, and ranked first in a search.
 *
 * **One list for both studios**, deliberately. The two pickers offer the same
 * blocks (`#/lib/studioBlockPicker` builds both from one list, FlashBook's rich
 * reading blocks included), and a creator's habits belong to the creator rather
 * than to whichever studio happens to be open: adding a Matching block to a
 * capsule is the same reach as adding it to a book. A key one picker does not
 * offer simply resolves to nothing when that picker builds its rows (see
 * `recentStudioPickerEntries`), so a shared list can never put a block in front of
 * someone in a sheet that cannot add it.
 *
 * This is device state, like the drafts indexes next door: a shortcut, not a
 * record of the work, and nothing here is authoritative.
 */
import {device} from '#/storage'

/**
 * How much history is worth offering.
 *
 * Six rows is about what the sheet shows at once at a row's height, so the group
 * stays a shortcut rather than becoming the scroll it was meant to save — and the
 * list is only ever about habits, which are shorter than a list of blocks anyway.
 */
const MAX_RECENT_BLOCKS = 6

/**
 * The recently used keys, newest first.
 *
 * Normalised on the way *out* rather than merely trusted, because this key is
 * persistent storage: it outlives app versions, and its rows are painted straight
 * from what it holds, so a value written by any writer — now or later — must not
 * reach the sheet as a shape it cannot read. Unknown keys are *kept* here and
 * resolved by the picker, because which blocks exist is a property of the picker
 * (this build's rows) and not of this list.
 */
export function getRecentStudioBlocks(): string[] {
  const stored = device.get(['studioRecentBlocks']) ?? []
  const recent: string[] = []
  const seen = new Set<string>()
  for (const key of stored) {
    if (typeof key !== 'string' || !key.trim() || seen.has(key)) continue
    seen.add(key)
    recent.push(key)
    if (recent.length === MAX_RECENT_BLOCKS) break
  }
  return recent
}

/**
 * Records that a block was used, and hands back the new list.
 *
 * Re-adding a block a creator already has moves it to the front rather than
 * listing it twice: the order *is* the claim, so the block they just reached for
 * is the first thing the sheet offers back.
 */
export function rememberStudioBlock(key: string): string[] {
  const next = [
    key,
    ...getRecentStudioBlocks().filter(used => used !== key),
  ].slice(0, MAX_RECENT_BLOCKS)
  device.set(['studioRecentBlocks'], next)
  return next
}
